import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  User,
  Mail,
  CreditCard,
  Phone,
  MapPin,
  Eye,
  Search,
  Edit,
} from "lucide-react";
import { showSuccess, showError } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";
import { formatDate } from "@/utils/dateUtils";

const defaultValues = {
  name: "",
  email: "",
  identificationNumber: "",
  dateOfBirth: "",
  phone: "",
  address: "",
  emergencyContact: {
    name: "",
    phone: "",
    relationship: "",
  },
};

// Reusable Patient Form Component
function PatientForm({ onSubmit, onCancel, isLoading, isEditMode, defaultValues: formDefaults }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: formDefaults || defaultValues,
  });

  // Reset form when defaultValues change (for edit mode)
  useEffect(() => {
    if (formDefaults) {
      reset(formDefaults);
    }
  }, [formDefaults, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            Full Name *
          </label>
          <Input
            {...register("name", { required: "Name is required" })}
            placeholder="Enter patient's full name"
            data-testid="patient-name-input"
          />
          {errors.name && (
            <p className="text-xs text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address *
          </label>
          <Input
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address",
              },
            })}
            placeholder="patient@example.com"
            data-testid="patient-email-input"
          />
          {errors.email && (
            <p className="text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            ID Number {!isEditMode && "*"}
          </label>
          {isEditMode ? (
            <>
              <Input
                {...register("identificationNumber")}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500">
                ID number cannot be changed after creation
              </p>
            </>
          ) : (
            <>
              <Input
                {...register("identificationNumber", {
                  required: "ID number is required",
                })}
                placeholder="8501015009087"
                data-testid="patient-id-input"
              />
              {errors.identificationNumber && (
                <p className="text-xs text-red-500">
                  {errors.identificationNumber.message}
                </p>
              )}
            </>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">
            Date of Birth {!isEditMode && "*"}
          </label>
          {isEditMode ? (
            <>
              <Input
                type="date"
                {...register("dateOfBirth")}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500">
                Date of birth cannot be changed after creation
              </p>
            </>
          ) : (
            <>
              <Input
                type="date"
                {...register("dateOfBirth", {
                  required: "Date of birth is required",
                })}
                data-testid="patient-dob-input"
              />
              {errors.dateOfBirth && (
                <p className="text-xs text-red-500">
                  {errors.dateOfBirth.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone Number
          </label>
          <Input
            {...register("phone")}
            placeholder="+27 123 456 7890"
            data-testid="patient-phone-input"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Address
          </label>
          <Input
            {...register("address")}
            placeholder="Street address"
            data-testid="patient-address-input"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-medium">Emergency Contact</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              {...register("emergencyContact.name")}
              placeholder="Emergency contact name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Phone</label>
            <Input
              {...register("emergencyContact.phone")}
              placeholder="Emergency contact phone"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Relationship</label>
            <Input
              {...register("emergencyContact.relationship")}
              placeholder="e.g., Spouse, Parent"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} data-testid="patient-submit-btn">
          {isLoading
            ? isEditMode
              ? "Saving..."
              : "Creating..."
            : isEditMode
              ? "Save Changes"
              : "Create Patient"}
        </Button>
      </div>
    </form>
  );
}

export default function PatientManagement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch patients
  const {
    data: patients,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["patients"],
    queryFn: () => apiClient.get("/api/patients"),
  });

  // Create patient mutation
  const createPatient = useMutation({
    mutationFn: (patientData) => apiClient.post("/api/patients", patientData),
    onSuccess: () => {
      queryClient.invalidateQueries(["patients"]);
      setIsAddDialogOpen(false);
      showSuccess("Patient created successfully");
    },
    onError: (error) => {
      showError("Failed to create patient", getErrorMessage(error));
    },
  });

  // Update patient mutation
  const updatePatient = useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`/api/patients/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["patients"]);
      setEditingPatient(null);
      showSuccess("Patient updated successfully");
    },
    onError: (error) => {
      showError("Failed to update patient", getErrorMessage(error));
    },
  });

  const handleCreate = (data) => {
    createPatient.mutate(data);
  };

  const handleUpdate = (data) => {
    updatePatient.mutate({ id: editingPatient.id, data });
  };

  const handleEditClick = (patient) => {
    // Format dateOfBirth for input field (YYYY-MM-DD)
    let dateOfBirth = "";
    if (patient.dateOfBirth) {
      const date = new Date(patient.dateOfBirth);
      dateOfBirth = date.toISOString().split("T")[0];
    }

    setEditingPatient({
      ...patient,
      dateOfBirth,
      emergencyContact: {
        name: patient.emergencyContact?.name || "",
        phone: patient.emergencyContact?.phone || "",
        relationship: patient.emergencyContact?.relationship || "",
      },
    });
  };

  if (isLoading) return <div>Loading patients...</div>;
  if (fetchError)
    return <div>Error loading patients: {fetchError.message}</div>;

  // Filter patients based on search term
  const filteredPatients =
    patients?.filter(
      (patient) =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.identificationNumber.includes(searchTerm) ||
        patient.phone?.includes(searchTerm)
    ) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Patient Management</h1>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Patient</DialogTitle>
            </DialogHeader>
            <PatientForm
              onSubmit={handleCreate}
              onCancel={() => setIsAddDialogOpen(false)}
              isLoading={createPatient.isPending}
              isEditMode={false}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Patient Dialog */}
      <Dialog
        open={!!editingPatient}
        onOpenChange={(open) => {
          if (!open) setEditingPatient(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Patient</DialogTitle>
          </DialogHeader>
          {editingPatient && (
            <PatientForm
              onSubmit={handleUpdate}
              onCancel={() => setEditingPatient(null)}
              isLoading={updatePatient.isPending}
              isEditMode={true}
              defaultValues={editingPatient}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Search Bar */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search patients by name, email, ID, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="patient-search-input"
          />
        </div>
        {searchTerm && (
          <div className="text-sm text-gray-500">
            {filteredPatients.length} of {patients?.length || 0} patients
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Patients</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPatients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm
                ? `No patients found matching "${searchTerm}"`
                : "No patients found. Add your first patient to get started."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>ID Number</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatients.map((patient, index) => (
                  <TableRow key={patient.id} data-testid={`patient-row-${index}`}>
                    <TableCell className="font-medium">{patient.name}</TableCell>
                    <TableCell>{patient.email}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {patient.identificationNumber}
                    </TableCell>
                    <TableCell>{patient.phone || "—"}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Active
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(patient.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditClick(patient)}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/patients/${patient.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
